import { useEffect, useMemo, useRef, useState } from "react";
import { periodContains, periodForNow, periodLabel } from "@/lib/dashboard-period";
import { isSalesProject, pickClosedDate } from "@/lib/offer-closed-date";
import { calcSalesDisplayMetrics } from "@/lib/sales-display-metrics";
import { useSalesDashboardData } from "@/hooks/use-sales-dashboard-data";
import { useTodaysBirthdays } from "@/hooks/use-birthdays";
import { useSalesDisplaySlides } from "@/hooks/use-sales-display-slides";
import { useSalesUnitHourlyRates } from "@/hooks/use-sales-unit-hourly-rates";
import { useTopProjects } from "@/hooks/use-top-projects";
import {
  birthdayNotification,
  buildDisplayPeopleByProject,
  detectSalesDisplayNotifications,
  salesDisplayPreviewNotifications,
  type SalesDisplayNotification,
  type SalesDisplaySnapshot,
} from "@/lib/sales-display-notifications";
import { TVFooter } from "@/components/display/TVFooter";
import { DisplayKpiSlide } from "@/components/dashboard-sales/display/DisplayKpiSlide";
import { DisplaySalesPulseSlide } from "@/components/dashboard-sales/display/DisplaySalesPulseSlide";
import { DisplaySalesUnitSlide } from "@/components/dashboard-sales/display/DisplaySalesUnitSlide";
import { DisplayHallOfFameSlide } from "@/components/dashboard-sales/display/DisplayHallOfFameSlide";
import { DisplayNotificationSlide } from "@/components/dashboard-sales/display/DisplayNotificationSlide";
import { ConfiguredSalesDisplaySlide } from "@/components/display/ConfiguredSalesDisplaySlide";
import { configuredSalesDisplayLoop } from "@/lib/sales-display-loop";
import {
  SALES_DISPLAY_PREVIEW_PARAM,
  SALES_DISPLAY_PREVIEW_VALUE,
  isSalesDisplayPreviewCommand,
  salesDisplayPreviewScreens,
  type SalesDisplayPreviewState,
} from "@/lib/sales-display-preview";
import { cn } from "@/lib/utils";

const REFETCH_MS = 60_000;

export default function SalesDisplayPage() {
  const eventPreviewEnabled = useMemo(
    () => new URLSearchParams(window.location.search).get("preview") === "hendelser",
    [],
  );
  // VIB-2705: satt når siden kjører i preview-ruta i slideoppsettet. Da skal
  // loopen stå stille og styres utenfra, ikke bytte skjerm av seg selv.
  const setupPreviewEnabled = useMemo(
    () =>
      new URLSearchParams(window.location.search).get(SALES_DISPLAY_PREVIEW_PARAM) ===
      SALES_DISPLAY_PREVIEW_VALUE,
    [],
  );
  const { data: configuredSlides = [] } = useSalesDisplaySlides();
  const now = useMemo(() => new Date(), []);
  const yearPeriod = useMemo(() => periodForNow("year", now), [now]);
  const monthPeriod = useMemo(() => periodForNow("month", now), [now]);
  const {
    projects,
    owners,
    salesUnits,
    people,
    projectMeta,
    isLoading: salesDashboardLoading,
  } = useSalesDashboardData({ refetchInterval: REFETCH_MS });
  const { data: birthdays = [] } = useTodaysBirthdays();
  const { data: topProjects } = useTopProjects(true);
  const { data: salesUnitHourlyRates = [] } = useSalesUnitHourlyRates(yearPeriod);

  const nameMap = useMemo(() => new Map(people.map((person) => [person.userId, person.name])), [people]);
  const avatarMap = useMemo(() => new Map(people.map((person) => [person.userId, person.avatarUrl])), [people]);
  const nameOf = (id: string) => nameMap.get(id) ?? "Ukjent";
  const avatarOf = (id: string) => avatarMap.get(id) ?? null;
  const metrics = useMemo(
    () => calcSalesDisplayMetrics(projects, owners, salesUnits, yearPeriod, monthPeriod),
    [monthPeriod, owners, projects, salesUnits, yearPeriod],
  );
  const monthLabel = periodLabel(monthPeriod);

  const slides = useMemo(
    () => configuredSalesDisplayLoop(configuredSlides, metrics.units.map((unit) => unit.unitId)),
    [configuredSlides, metrics.units],
  );
  const [slideIndex, setSlideIndex] = useState(0);
  const [notificationQueue, setNotificationQueue] = useState<SalesDisplayNotification[]>([]);
  const [previewIndex, setPreviewIndex] = useState(0);
  const previewNotifications = useMemo(
    () => salesDisplayPreviewNotifications(people, salesUnits),
    [people, salesUnits],
  );
  const currentNotification = eventPreviewEnabled
    ? previewNotifications[previewIndex % Math.max(previewNotifications.length, 1)] ?? null
    : notificationQueue[0] ?? null;
  const currentSlide = slides[slideIndex % slides.length];

  useEffect(() => {
    // I oppsett-previewen blar man selv. Et skjermbytte midt i en vurdering av
    // hvordan en slide ser ut, er ikke en preview.
    if (setupPreviewEnabled || currentNotification || slides.length <= 1) return;
    const id = setTimeout(
      () => setSlideIndex((index) => (index + 1) % slides.length),
      currentSlide.durationSeconds * 1000,
    );
    return () => clearTimeout(id);
  }, [currentNotification, currentSlide.durationSeconds, currentSlide.id, setupPreviewEnabled, slides.length]);

  // VIB-2705: foreldren (slideoppsettet) styrer posisjonen, og får vite hvor
  // loopen står. Samme origin, men origin sjekkes uansett — en iframe tar imot
  // meldinger fra hvem som helst.
  useEffect(() => {
    if (!setupPreviewEnabled) return;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (!isSalesDisplayPreviewCommand(event.data)) return;
      const total = slides.length;
      if (total === 0) return;
      setSlideIndex((index) => {
        if (event.data.action === "next") return (index + 1) % total;
        if (event.data.action === "prev") return (index - 1 + total) % total;
        return Math.min(Math.max(event.data.index, 0), total - 1);
      });
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [setupPreviewEnabled, slides.length]);

  const unitNameById = useMemo(
    () => new Map(metrics.units.map((unit) => [unit.unitId, unit.unitName])),
    [metrics.units],
  );

  useEffect(() => {
    // Ingen sjekk på at vi faktisk står i en iframe: utenfor en er `window.parent`
    // oss selv, og da går meldingen til en lytter som ikke finnes. Å gjøre
    // ingenting er like riktig, og betingelsen ville bare vært en gren uten test.
    if (!setupPreviewEnabled) return;
    const message: SalesDisplayPreviewState = {
      type: "sales-display-preview/state",
      index: slides.length === 0 ? 0 : slideIndex % slides.length,
      screens: salesDisplayPreviewScreens(slides, unitNameById),
    };
    window.parent.postMessage(message, window.location.origin);
  }, [setupPreviewEnabled, slideIndex, slides, unitNameById]);

  useEffect(() => {
    if (!currentNotification) return;
    const id = setTimeout(
      () => {
        if (eventPreviewEnabled) {
          setPreviewIndex((index) => (index + 1) % previewNotifications.length);
        } else {
          setNotificationQueue((queue) => queue.slice(1));
        }
      },
      currentNotification.durationSeconds * 1000,
    );
    return () => clearTimeout(id);
  }, [currentNotification, eventPreviewEnabled, previewNotifications.length]);

  const displaySnapshot = useMemo((): SalesDisplaySnapshot => {
    const ownersByProject = buildDisplayPeopleByProject(owners, salesUnits);
    return {
      closedDeals: projects
        .filter((project) => isSalesProject(project) && periodContains(yearPeriod, pickClosedDate(project, project.offer_accepted_at)))
        .map((project) => ({
          projectId: project.id,
          amount: Number(project.total_amount ?? 0),
          acceptedAt: project.offer_accepted_at ?? project.created_at,
          ownerIds: ownersByProject.get(project.id) ?? [],
        })),
      achievers: metrics.units
        .filter((unit) => (unit.achievementPct ?? 0) >= 100)
        .map((unit) => ({ id: unit.unitId, name: unit.unitName, memberIds: unit.memberUserIds })),
    };
  }, [metrics.units, owners, projects, salesUnits, yearPeriod]);

  const previousDisplaySnapshot = useRef<SalesDisplaySnapshot | null>(null);
  useEffect(() => {
    // Preview-ruta skal vise loopen, ikke bli avbrutt av en hendelse som
    // tilfeldigvis inntreffer mens noen setter opp slides.
    if (eventPreviewEnabled || setupPreviewEnabled || salesDashboardLoading) return;
    const notifications = detectSalesDisplayNotifications(
      previousDisplaySnapshot.current,
      displaySnapshot,
      nameOf,
      (projectId) => projectMeta.get(projectId)?.name ?? "Nytt prosjekt",
      avatarOf,
    );
    previousDisplaySnapshot.current = displaySnapshot;
    if (notifications.length > 0) setNotificationQueue((queue) => [...queue, ...notifications]);
    // Maps are represented by the snapshot inputs; lookup values are embedded in event payloads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displaySnapshot, eventPreviewEnabled, salesDashboardLoading, setupPreviewEnabled]);

  const shownBirthdays = useRef(new Set<string>());
  useEffect(() => {
    if (eventPreviewEnabled || setupPreviewEnabled || birthdays.length === 0) return;
    const dateKey = now.toISOString().slice(0, 10);
    const notification = birthdayNotification(birthdays, dateKey);
    if (!notification || shownBirthdays.current.has(notification.id)) return;
    shownBirthdays.current.add(notification.id);
    setNotificationQueue((queue) => [...queue, notification]);
  }, [birthdays, eventPreviewEnabled, now, setupPreviewEnabled]);

  const slideContent = () => {
    if (currentSlide.kind === "message" || currentSlide.kind === "image" || currentSlide.kind === "video") {
      return <ConfiguredSalesDisplaySlide slide={currentSlide.slide} />;
    }
    if (currentSlide.kind === "sales-pulse") {
      return <DisplaySalesPulseSlide metric={metrics.team} monthLabel={monthLabel} />;
    }
    if (currentSlide.kind === "sales-unit") {
      const metric = metrics.units.find((unit) => unit.unitId === currentSlide.unitId);
      return metric ? <DisplaySalesUnitSlide metric={metric} people={people} monthLabel={monthLabel} year={yearPeriod.year} /> : null;
    }
    if (currentSlide.kind === "hall-of-fame-timepris") {
      return <DisplayHallOfFameSlide variant="timepris" rows={topProjects?.timeprisRanking ?? []} />;
    }
    if (currentSlide.kind === "hall-of-fame-geek") {
      return <DisplayHallOfFameSlide variant="geek-under-budget" rows={topProjects?.bfRanking ?? []} />;
    }
    if (currentSlide.kind === "hall-of-fame-sales-unit-rate") {
      return <DisplayHallOfFameSlide variant="sales-unit-hourly-rate" rows={salesUnitHourlyRates} />;
    }
    return (
      <DisplayKpiSlide
        achievedGeekKr={metrics.team.achievedGeekKr}
        annualTargetKr={metrics.team.annualTargetKr}
        achievementPct={metrics.team.achievementPct}
        year={yearPeriod.year}
      />
    );
  };

  const advanceDisplay = () => {
    if (currentNotification) {
      if (eventPreviewEnabled) {
        setPreviewIndex((index) => (index + 1) % previewNotifications.length);
      } else {
        setNotificationQueue((queue) => queue.slice(1));
      }
      return;
    }
    setSlideIndex((index) => (index + 1) % slides.length);
  };

  const isImmersive = Boolean(currentNotification)
    || currentSlide.kind === "message"
    || currentSlide.kind === "image"
    || currentSlide.kind === "video";
  return (
    <div className={cn("relative flex h-screen flex-col overflow-hidden bg-background", isImmersive ? "" : "p-12")}>
      {currentNotification ? (
        <main className="min-h-0 flex-1"><DisplayNotificationSlide notification={currentNotification} /></main>
      ) : (
        <>
          <header className="flex items-baseline justify-between">
            <h1 className="text-3xl font-bold tracking-tight">Salg · Sølvrev</h1>
            <span className="text-2xl text-muted-foreground">{yearPeriod.year}</span>
          </header>
          <main className="min-h-0 flex-1">{slideContent()}</main>
        </>
      )}

      <div className={cn(isImmersive && "absolute inset-x-8 bottom-3 z-20")}>
        <TVFooter variant={isImmersive ? "dark" : "light"} />
      </div>
      <button
        type="button"
        aria-label="Vis neste slide"
        tabIndex={-1}
        onClick={advanceDisplay}
        className="absolute inset-0 z-[60] cursor-default opacity-0"
      />
    </div>
  );
}
